/**
 * Pure helpers for Explore Search (aris-ai plan 47 §4). No React, no network.
 *
 * The backend owns relevance and order: every list here keeps the array order it was
 * given. These helpers only decide which part of the response a tab shows.
 */

export const SEARCH_TAB = {
  ALL: "all",
  DIALOGUES: "dialogues",
  LIFE_AREAS: "life_areas",
  COLLECTIONS: "collections",
};

export const SEARCH_TABS = [
  { key: SEARCH_TAB.ALL, label: "All" },
  { key: SEARCH_TAB.DIALOGUES, label: "Dialogues" },
  { key: SEARCH_TAB.LIFE_AREAS, label: "Life Areas" },
  { key: SEARCH_TAB.COLLECTIONS, label: "Collections" },
];

/** How many Dialogues the All tab shows before "See All". */
export const TOP_RESULTS = 3;
/** The page size "See All" asks for (the backend caps it). */
export const SEE_ALL_LIMIT = 50;

/** A response with every list present, so renderers never guard each key. */
export const normaliseSearch = (data) => ({
  query: data?.query || "",
  adequate: Boolean(data?.adequate),
  dialogues: Array.isArray(data?.dialogues)
    ? data.dialogues
    : Array.isArray(data?.results)
      ? data.results
      : [],
  lifeAreas: Array.isArray(data?.life_areas) ? data.life_areas : [],
  collections: Array.isArray(data?.collections) ? data.collections : [],
  // DCI v27 (aris-ai plan 53): the member's own Personal Dialogues, never mixed in.
  personal: {
    items: Array.isArray(data?.personal_dialogues?.items)
      ? data.personal_dialogues.items
      : [],
    total: Number(data?.personal_dialogues?.total) || 0,
  },
  related: (Array.isArray(data?.related_searches) ? data.related_searches : [])
    .map((s) => String(s || "").trim())
    .filter(Boolean),
  talkItThrough: data?.talk_it_through || null,
});

/** The `family` of a Collection, read from its drill-in href (or its title). */
export const familyOf = (collection) => {
  const href = String(collection?.href || "");
  const at = href.indexOf("?");
  if (at >= 0) {
    const family = new URLSearchParams(href.slice(at + 1)).get("family");
    if (family) return family;
  }
  return collection?.title || "";
};

/** Where a Dialogue result opens: its Overview, research or personal alike. */
export const dialogueRoute = (item) =>
  item?.dialogue_id ? `/explore/dialogue/${encodeURIComponent(item.dialogue_id)}` : null;

/** Where a Life Area result opens (Self-Knowledge has its own page). */
export const lifeAreaRoute = (area) =>
  area?.key === "SELF_KNOWLEDGE"
    ? "/explore/self-knowledge"
    : area?.key
      ? `/explore/life-areas/${encodeURIComponent(area.key)}`
      : null;

/** The secondary line of a result: a Personal Dialogue's source, else its Life Area. */
export const resultMeta = (item) => {
  if (item?.type === "personal") {
    return item?.source?.line || "Personal Dialogue";
  }
  return item?.display_area_label || "";
};

/** Where "See All" for Personal Dialogues opens: the library, searched by the query. */
export const personalSeeAllRoute = (query) =>
  `/explore/my-dialogues?q=${encodeURIComponent(query || "")}`;
